"use client"

import Link from "next/link"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Home, LogOut, Upload } from "lucide-react"
import { Menu, Book, Info, HelpCircle, Shield, UserPlus, LogIn } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { ModeToggle } from "@/components/theme-toggle"
import React, {useEffect} from "react"
import { useState } from "react"
import { usePathname, useRouter } from "next/navigation"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import {Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger} from "@/components/ui/sheet"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { registerUser} from "@/lib/firestore";
import {toast} from "@/hooks/use-toast";
import {auth} from "@/lib/firebase";
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getCookie } from 'cookies-next';

function MobileNav({
                       items,
                       onClose,
                   }: {
    items: { href: string; label: string; icon: React.ReactNode }[]
    onClose?: () => void
}) {
    const pathname = usePathname()

    return (
        <div className="flex flex-col space-y-3">
            {items.map((item) => (
                <Link
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    className={`flex items-center rounded-lg p-2 text-sm font-medium hover:bg-accent ${
                        pathname === item.href ? "bg-accent" : "transparent"
                    }`}
                >
                    {item.icon}
                    <span className="ml-3">{item.label}</span>
                </Link>
            ))}
        </div>
    )
}

interface UserData{
    email: string;
    nickname: string;
    role: string;
    favorites: string[];
    dateCreated: string;
    avatar: string;
}

export function Header() {
    const router = useRouter()
    const [isAdmin, setIsAdmin] = useState(false)
    const [loginOpen, setLoginOpen] = useState(false)
    const [registerOpen, setRegisterOpen] = useState(false)
    const [forgotOpen, setForgotOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const [isLoggedIn, setIsLoggedIn] = useState(false);

    // Form states
    const [username, setUsername] = useState("")
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")

    const toggleMenu = () => setMenuOpen(!menuOpen)

    useEffect(() => {
        const token = getCookie('__session')
        //console.log('Token:', token)
        setIsLoggedIn(!!token)
    }, [])

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const userCredential = await signInWithEmailAndPassword(auth, username, password);
            const idToken = await userCredential.user.getIdToken();

            const res = await fetch('/api/session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken }),
            });

            if (!res.ok) {
                throw new Error('No se pudo establecer la sesión');
            }

            toast({
                title: "Inicio de sesión exitoso",
                description: "Bienvenido de nuevo.",
                variant: "default",
            });

            setIsLoggedIn(true);
            setLoginOpen(false);
            setUsername("");
            setPassword("");

        } catch (err) {
            console.error(err);
            toast({
                title: "Error de inicio de sesión",
                description: "Usuario o contraseña incorrectos.",
                variant: "destructive",
            });
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault()
        // Here you would implement actual registration
        if (password !== confirmPassword) {
            toast({
                title: "Error de registro",
                description: "Las contraseñas no coinciden.",
                variant: "destructive",
            })
            return
        }

        // Llamar a la función de registro aquí
        const register = await registerUser(username, email, password);

        if (register) {
            toast({
                title: "Registro exitoso",
                description: "Ahora puedes iniciar sesión.",
                variant: "default",
            })
        } else{
            toast({
                title: "Error de registro",
                description: "Sucedió un error al registrar la cuenta.",
                variant: "destructive",
            })
        }

        setIsLoggedIn(true)
        setRegisterOpen(false)
        // Reset form fields
        setUsername("")
        setEmail("")
        setPassword("")
        setConfirmPassword("")
    }

    const handleLogout = async () => {
        await fetch('/api/logout', {
            method: 'POST',
        })

        setIsLoggedIn(false)
        setIsAdmin(false)

        toast({
            title: "Sesión cerrada",
            description: "Has cerrado sesión correctamente.",
            variant: "default",
        })

        // Opcional: recargar la UI para reflejar cambios
        //router.refresh()
    }

    const handleForgotPassword = (e: React.FormEvent) => {
        e.preventDefault()
        // Here you would implement password recovery
        setForgotOpen(false)
        // Reset form field
        setEmail("")
    }

    const navItems = [
        { href: "/", label: "Inicio", icon: <Home className="w-5 h-5" /> },
        { href: "/search", label: "Directorio", icon: <Book className="w-5 h-5" /> },
        { href: "/unete", label: "Únete", icon: <UserPlus className="w-5 h-5" /> },
        { href: "/info", label: "Info", icon: <Info className="w-5 h-5" /> },
        { href: "/faq", label: "FAQ", icon: <HelpCircle className="w-5 h-5" /> },
        { href: "/dmca", label: "DMCA", icon: <Shield className="w-5 h-5" /> },
    ]

    const [isOpen, setIsOpen] = useState(false)

    return (
        <header className="sticky top-0 z-50 w-full border-b dark:border-zinc-800 border-zinc-400 bg-zinc-100/95 dark:bg-zinc-900/95 backdrop-blur supports-[backdrop-filter]:bg-zinc-100/60 dark:supports-[backdrop-filter]:bg-zinc-900/60">
            <div className="container flex h-14 items-center">
                <Link href="/" className="flex items-center space-x-2">
                    <img src="/logo.png" alt="Logo del sitio" className="h-10 w-10" />
                    <span className="font-bold text-xl text-red-500 hidden md:block">MandaloAsiNoma</span>
                </Link>
                <nav className="hidden md:flex items-center space-x-6 text-sm font-medium mx-6">
                    {navItems.map((item) => (
                        <Link key={item.href} href={item.href} className="hover:text-red-500">
                            {item.label}
                        </Link>
                    ))}
                </nav>
                {/* Theme Toggle & Mobile Menu Button */}
                <div className="flex flex-1 items-center justify-end space-x-1 md:space-x-2">
                    <div className="mr-3">
                        <ModeToggle />
                    </div>
                    <div className="flex pr-3 items-center ml-auto space-x-4">
                        {isLoggedIn ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-red-400">
                                        <Avatar className="h-8 w-8">
                                            <AvatarImage src="/m.jpg" alt="@usuario" />
                                            <AvatarFallback className="bg-red-500 text-xl">{localStorage.getItem("nickname")?.at(0)!.toUpperCase()}</AvatarFallback>
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="bg-zinc-900 text-white border-zinc-800 shadow-lg">
                                    <DropdownMenuItem asChild>
                                        <Link href="/admin/my-uploads" className="flex items-center space-x-2">
                                            <Upload className="h-4 w-4" />
                                            <span>Mis Publicaciones</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleLogout} className="flex items-center space-x-2">
                                        <LogOut className="h-4 w-4" />
                                        <span>Cerrar Sesión</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center space-x-2">
                                {/* Login Icon Button */}
                                <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
                                    <DialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="p-2 rounded-md border border-zinc-300 dark:border-zinc-700 text-foreground
             hover:bg-red-500/10 hover:border-red-500 hover:text-red-500 transition">
                                            <LogIn className="h-5 w-5" />
                                            <span className="sr-only">Iniciar Sesión</span>
                                        </Button>
                                    </DialogTrigger>
                                    {/* Login Popup */}
                                    <DialogContent className="sm:max-w-[425px] bg-zinc-900 text-white border-zinc-800 shadow-lg transition-all duration-300 ease-in-out">
                                        <DialogHeader>
                                            <DialogTitle className="text-xl font-semibold text-center">Iniciar Sesión</DialogTitle>
                                        </DialogHeader>
                                        <form onSubmit={handleLogin} className="grid gap-4 py-4">
                                            <div className="grid gap-2">
                                                <Input
                                                    id="username"
                                                    placeholder="Usuario o Correo"
                                                    className="bg-zinc-800 border-zinc-700"
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="grid gap-2">
                                                <Input
                                                    id="password"
                                                    type="password"
                                                    placeholder="Contraseña"
                                                    className="bg-zinc-800 border-zinc-700"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            {/*<div className="text-right">*/}
                                            {/*    <button*/}
                                            {/*        type="button"*/}
                                            {/*        className="text-sm text-zinc-400 hover:text-white"*/}
                                            {/*        onClick={() => {*/}
                                            {/*            setLoginOpen(false)*/}
                                            {/*            setForgotOpen(true)*/}
                                            {/*        }}*/}
                                            {/*    >*/}
                                            {/*        ¿Olvidaste tu contraseña?*/}
                                            {/*    </button>*/}
                                            {/*</div>*/}
                                            <Button type="submit" className="w-full bg-red-500 hover:bg-red-600">
                                                Iniciar Sesión
                                            </Button>
                                            {/*<div className="text-center text-sm text-zinc-400">*/}
                                            {/*    ¿No tienes una cuenta?*/}
                                            {/*    <button*/}
                                            {/*        type="button"*/}
                                            {/*        className="text-white hover:underline ml-1"*/}
                                            {/*        onClick={() => {*/}
                                            {/*            setLoginOpen(false)*/}
                                            {/*            setRegisterOpen(true)*/}
                                            {/*        }}*/}
                                            {/*    >*/}
                                            {/*        Registrarse*/}
                                            {/*    </button>*/}
                                            {/*</div>*/}
                                        </form>
                                    </DialogContent>
                                </Dialog>

                                {/* Register Icon Button */}
                                <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
                                    {/* Register Popup */}
                                    <DialogContent className="sm:max-w-[425px] bg-zinc-900 text-white border-zinc-800 shadow-lg transition-all duration-300 ease-in-out">
                                        <DialogHeader>
                                            <DialogTitle className="text-xl font-semibold text-center">Crear Cuenta</DialogTitle>
                                        </DialogHeader>
                                        <form onSubmit={handleRegister} className="grid gap-4 py-4">
                                            <div className="grid gap-2">
                                                <Input
                                                    id="new-username"
                                                    placeholder="Usuario"
                                                    className="bg-zinc-800 border-zinc-700"
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="grid gap-2">
                                                <Input
                                                    id="new-email"
                                                    type="email"
                                                    placeholder="Correo Electrónico"
                                                    className="bg-zinc-800 border-zinc-700"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="grid gap-2">
                                                <Input
                                                    id="new-password"
                                                    type="password"
                                                    placeholder="Contraseña"
                                                    className="bg-zinc-800 border-zinc-700"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="grid gap-2">
                                                <Input
                                                    id="confirm-password"
                                                    type="password"
                                                    placeholder="Confirmar Contraseña"
                                                    className="bg-zinc-800 border-zinc-700"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <Button type="submit" className="w-full bg-red-500 hover:bg-red-600">
                                                Registrarse
                                            </Button>
                                            <div className="text-center text-sm text-zinc-400">
                                                ¿Ya tienes una cuenta?
                                                <button
                                                    type="button"
                                                    className="text-white hover:underline ml-1"
                                                    onClick={() => {
                                                        setRegisterOpen(false)
                                                        setLoginOpen(true)
                                                    }}
                                                >
                                                    Iniciar Sesión
                                                </button>
                                            </div>
                                        </form>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        )}
                    </div>
                    <Sheet open={isOpen} onOpenChange={setIsOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="p-2 rounded-md border border-zinc-300 dark:border-zinc-700 text-foreground
             hover:bg-red-500/10 hover:border-red-500 hover:text-red-500 transition px-0 text-base focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden"
                            >
                                <Menu className="h-6 w-6" />
                                <span className="sr-only">Toggle Menu</span>
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="pr-0">
                            <SheetHeader>
                                <SheetTitle className={"mb-5"}>Navegación</SheetTitle>
                            </SheetHeader>
                            <MobileNav items={navItems} onClose={() => setIsOpen(false)} />
                        </SheetContent>
                    </Sheet>
                </div>
            </div>

            {/* Forgot Password Dialog */}
            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
                <DialogContent className="sm:max-w-[425px] bg-zinc-900 text-white border-zinc-800 shadow-lg transition-all duration-300 ease-in-out">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-center">Recuperar Contraseña</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleForgotPassword} className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Input
                                id="forgot-email"
                                type="email"
                                placeholder="Correo Electrónico"
                                className="bg-zinc-800 border-zinc-700"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <Button type="submit" className="w-full bg-red-500 hover:bg-red-600">
                            Enviar Enlace de Recuperación
                        </Button>
                        <div className="text-center text-sm text-zinc-400">
                            <button
                                type="button"
                                className="text-white hover:underline"
                                onClick={() => {
                                    setForgotOpen(false)
                                    setLoginOpen(true)
                                }}
                            >
                                Volver a Iniciar Sesión
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Mobile Navigation - Vertical Menu */}
            {menuOpen && (
                <nav className="flex flex-col items-center space-y-2 p-4 md:hidden bg-zinc-100 dark:bg-zinc-900 border-t dark:border-zinc-800 border-zinc-400">
                    <Link
                        href="/"
                        onClick={() => setMenuOpen(false)}
                        className="transition-colors dark:hover:text-red-400 hover:text-red-600 text-gray-900 dark:text-zinc-400"
                    >
                        Inicio
                    </Link>
                    <Link
                        href="/search"
                        onClick={() => setMenuOpen(false)}
                        className="transition-colors dark:hover:text-red-400 hover:text-red-600 text-gray-900 dark:text-zinc-400"
                    >
                        Directorio
                    </Link>
                </nav>
            )}
        </header>
    )
}

